import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { Link } from "react-router-dom";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { formatINR2, fmtDate } from "@/lib/format";
import { BACKEND_URL } from "@/lib/config";
import { Download, Trash2 } from "lucide-react";

export default function Payments() {
  const { brands, brandQuery } = useBrand();
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/payments", { params: brandQuery }),
      api.get("/invoices"),
    ]);
    setItems(a.data); setInvoices(b.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm("Reverse this payment?")) return;
    await api.delete(`/payments/${p.id}`); load();
  };

  return (
    <div data-testid="payments-page">
      <PageHeader
        eyebrow="Cash flow"
        title="Payments"
        description="Receivables collected, tied back to invoices."
        actions={
          <Button variant="outline" asChild className="border-border bg-white">
            <a href={`${BACKEND_URL}/api/exports/payments?fmt=xlsx`} target="_blank" rel="noreferrer">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </a>
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyState title="No payments recorded" />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Invoice</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Method</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reference</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Amount</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => {
                const inv = invoices.find((i) => i.id === p.invoice_id);
                const brand = brands.find((b) => b.id === p.brand_id);
                return (
                  <TableRow key={p.id} className="border-border hover:bg-[#f8f6f2]" data-testid={`payment-row-${p.id}`}>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(p.date)}</TableCell>
                    <TableCell><Link to={`/invoices/${p.invoice_id}`} className="font-mono text-bronze hover:underline">{inv?.invoice_number || "—"}</Link></TableCell>
                    <TableCell>{brand ? <Pill>{brand.code}</Pill> : "—"}</TableCell>
                    <TableCell className="text-sm">{p.method}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{p.reference || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{formatINR2(p.amount)}</TableCell>
                    <TableCell className="text-right"><button onClick={() => del(p)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
